// ink-component
import React, { HTMLAttributes, ReactNode } from 'react';
import styled from 'styled-components';
import Icon from '../../Icon/Icon';
import Button from '../../Button/Button';

import { modalTokens } from '../../../tokens/components/Modal/tokens';

import getGlobalProps from '../../../helpers/constants/global-props';

export interface ModalHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Defines Modal title. */
  title: ReactNode;
  /** Callback for Modal close button. */
  onClose: (event?: any) => void;
  /** Disables Modal close button while async processes are running. */
  isLoading?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests. */
  'data-testid'?: string | number;
}

const ModalHeaderWrapper = styled.div`
  align-items: baseline;
  display: flex;
  flex-direction: row;
  gap: ${modalTokens.HeaderContainerSpacingGapBaseDefault};
  justify-content: space-between;
  margin-bottom: ${modalTokens.HeaderContainerSpacingBottomBaseDefault};
`;

const ModalHeaderTitle = styled.h2`
  font-family: ${modalTokens.HeaderTitleFontFamilyBaseDefault};
  font-size: 24px;
  font-weight: ${modalTokens.HeaderTitleFontWeightBaseDefault};
  line-height: ${modalTokens.HeaderTitleFontLeadingBaseDefault};
  position: relative;
  top: -2px;
`;

// The close button should offset its padding, to visually align the X icon
const ModalHeaderAction = styled.div`
  margin-right: ${modalTokens.HeaderActionSpacingMarginRightBaseDefault};
  margin-top: ${modalTokens.HeaderActionSpacingMarginTopBaseDefault};
`;

const globalProps = getGlobalProps({});

/**
 * @purpose Arranges Modal title and close button
 * @group Modal
 */
const ModalHeader = ({
  className,
  style,
  title,
  onClose,
  isLoading,
  'data-testid': dataTestid,
  ...otherProps
}: ModalHeaderProps) => {
  return (
    <ModalHeaderWrapper {...otherProps} {...globalProps} data-testid={dataTestid}>
      <ModalHeaderTitle>{title}</ModalHeaderTitle>
      <ModalHeaderAction>
        <Button type="transparent" onClick={onClose} isLoading={isLoading}>
          <Icon type="close" size="xl" meaning="Close modal" />
        </Button>
      </ModalHeaderAction>
    </ModalHeaderWrapper>
  );
};

ModalHeader.displayName = 'Modal.Header';

export default ModalHeader;
